import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useAuth } from '../../auth/useAuth';

type Props = {
  title: string;
  onMenuClick?: () => void;
};

export function TopBar({ title, onMenuClick }: Props) {
  const { user, logout } = useAuth();

  const initials = user?.fullName
    ? user.fullName.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <header
      style={{
        height: 'var(--topbar-height)',
        background: 'var(--surface-base)',
        borderBottom: '1px solid var(--border-default)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 var(--page-x)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Hamburger — hidden on desktop via shell.css */}
        <button
          className="shell-hamburger"
          onClick={onMenuClick}
          aria-label="Abrir menú"
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
            <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-lg)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: 0,
            letterSpacing: '-0.02em',
          }}
        >
          {title}
        </h1>
      </div>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            aria-label="User menu"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-default)',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            {/* Avatar */}
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-full)',
                background: 'var(--brand-muted)',
                color: 'var(--text-brand)',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                letterSpacing: '0.02em',
              }}
            >
              {initials}
            </span>
            {/* Name hidden on very small screens */}
            <span
              className="topbar-username"
              style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: 500 }}
            >
              {user?.fullName ?? 'Admin'}
            </span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden style={{ color: 'var(--text-tertiary)' }}>
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            style={{
              minWidth: 160,
              background: 'var(--surface-base)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-raised)',
              padding: '4px',
              zIndex: 100,
              animation: 'fadeIn 120ms ease',
            }}
          >
            {user?.email && (
              <>
                <div
                  style={{
                    padding: '8px 10px',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-tertiary)',
                    borderBottom: '1px solid var(--border-subtle)',
                    marginBottom: 4,
                  }}
                >
                  {user.email}
                </div>
              </>
            )}
            <DropdownMenu.Item
              onSelect={logout}
              style={{
                padding: '7px 10px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-sm)',
                color: 'var(--badge-rejected-text)',
                cursor: 'pointer',
                outline: 'none',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--badge-rejected-bg)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  );
}
